import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UserState} from '../../api/models/user-state';
import {Subscription} from 'dexie';
import {GlobalApp} from '../../tools/global';
import {Router} from '@angular/router';
import {US_Interactive} from '../../api/models/us_interactive';
import {PowerbiEmbedComponent} from '../../elements/powerbi-embed/powerbi-embed.component';
import {PodLayoutEx} from '../../api/models/pod-layout-ex';
import {AdminInteractiveService} from '../../api/services/admin-interactive.service';
import {PodStatusSnackbarComponent, PrintOnDemandData} from '../../elements/pod-status-snackbar/pod-status-snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SubscriptionService} from '../../api/services/subscription.service';
import {MatSelectChange} from '@angular/material/select';
import {PrintOnDemandService} from '../../api/services/print-on-demand.service';
import {ConfirmDialogComponent} from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {DialogResultType} from '../../dialogs/DialogResult';
import {MatDialog} from '@angular/material/dialog';
import {PodRecurring} from '../../api/models/pod-recurring';
import {
  RecurringPodDialogComponent, RecurringPodDialogResult,
  RecurringPodType
} from '../../dialogs/recurring-pod-dialog/recurring-pod-dialog.component';

@Component({
  selector: "app-report-layouts",
  templateUrl: "./report-layouts.component.html"
})
export class ReportLayoutsComponent implements OnInit, OnDestroy {

  @ViewChild(PowerbiEmbedComponent) powerBi;

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false
  };
  userStateSubscription: Subscription = null;

  layoutInteractives: US_Interactive[] = [];
  selectedInteractive: US_Interactive | undefined;

  public loadingData = false;
  public showPowerBi = false;

  constructor(
    private app: GlobalApp,
    private router: Router,
    private adminInteractiveService: AdminInteractiveService,
    private subscriptionService: SubscriptionService,
    private printOnDemandService: PrintOnDemandService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      } else {
        this.app.getAuthorizationStatus().subscribe((result) => {
          this.app.refreshUserState(this.subscriptionService);
        });
      }
    });
  }

  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;


      this.layoutInteractives = userState.interactives.filter(interactive => interactive.allowPrintOnDemand);
      if (this.layoutInteractives.length > 0) {

        const lastStatus = this.app.getLastStatus();

        if (lastStatus.lastPodLayoutInteractiveId) {
          this.selectedInteractive = this.layoutInteractives.find(x => x.interactId === lastStatus.lastPodLayoutInteractiveId);
        }

        if (!this.selectedInteractive) {
          this.selectedInteractive = this.layoutInteractives[0];
        }
      } else {
        this.selectedInteractive = undefined;
      }
    });
  }

  ngOnDestroy() {
    if (this.userStateSubscription) {
      this.userStateSubscription.unsubscribe();
    }
  }

  createLayout() {
    this.powerBi.loadPodLayout(undefined, this.selectedInteractive.interactId, this.userState);
    this.showPowerBi = true;
    return false;
  }

  editLayout(layout: PodLayoutEx) {
    this.powerBi.loadPodLayout(layout, layout.interactId, this.userState);
    this.showPowerBi = true;
    return false;
  }

  deleteLayout(layout: PodLayoutEx) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this layout?"
    ).subscribe((result) => {
      if (result.ResultType == DialogResultType.Ok) {
        this.app.pushLoading("Deleting Layout...");
        this.printOnDemandService.printOnDemandDeletePrintOnDemandLayout(layout.podLayoutId).subscribe((_) => {
          this.app.refreshUserState(this.subscriptionService, () => {
            this.app.popLoading();
            this.snackBar.open("Layout '" + layout.name + "' Deleted.", null, {
              duration: 5000
            });
          });
        });
      }
    });
    return false;
  }

  createRecurring(layout: PodLayoutEx) {
    RecurringPodDialogComponent.showDialog(
      this.dialog,
    -1,
    layout.podLayoutId,
    RecurringPodType.Weekly,
    'example@example.com',
      0,
      1
    ).subscribe((result) => {
      if(result.ResultType == DialogResultType.Ok) {
        const results: RecurringPodDialogResult = result.ResultData;
        this.app.pushLoading("Saving Scheduled Report...");
        this.printOnDemandService.printOnDemandCreateRecurringPod({
          podRecurringId: -1,
          podLayoutId: results.podLayoutId,
          emailTo: results.emailTo,
          recurringType: results.recurringType,
          dayOfWeek: results.dayOfWeek,
          dayOfMonth: results.dayOfMonth
        }).subscribe((result) => {
          this.app.refreshUserState(this.subscriptionService, () => {
            this.app.popLoading();
            this.snackBar.open("Scheduled Report Saved.", null, {
              duration: 5000
            });
          });
        });
      }
    });
    return false;
  }

  editRecurring(recurring: PodRecurring) {
    RecurringPodDialogComponent.showDialog(
      this.dialog,
      recurring.podRecurringId,
      recurring.podLayoutId,
      recurring.recurringType,
      recurring.emailTo,
      recurring.dayOfWeek,
      recurring.dayOfMonth
    ).subscribe((result) => {
      if(result.ResultType == DialogResultType.Ok) {
        const results: RecurringPodDialogResult = result.ResultData;
        this.app.pushLoading("Saving Scheduled Report...");
        this.printOnDemandService.printOnDemandUpdateRecurringPod({
          podRecurringId: results.podRecurringId,
          request: {
            podRecurringId: results.podRecurringId,
            podLayoutId: results.podLayoutId,
            emailTo: results.emailTo,
            recurringType: results.recurringType,
            dayOfWeek: results.dayOfWeek,
            dayOfMonth: results.dayOfMonth
          }
        }).subscribe((result) => {
          this.app.refreshUserState(this.subscriptionService, () => {
            this.app.popLoading();
            this.snackBar.open("Scheduled Report Updated.", null, {
              duration: 5000
            });
          });
        });
      }
    });
    return false;
  }

  deleteRecurring(recurring: PodRecurring) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this recurring export?"
    ).subscribe((result) => {
      if (result.ResultType == DialogResultType.Ok) {
        this.app.pushLoading("Deleting Recurring Export...");
        this.printOnDemandService.printOnDemandDeleteRecurringPod(recurring.podRecurringId).subscribe((_) => {
          this.app.refreshUserState(this.subscriptionService, () => {
            this.app.popLoading();
            this.snackBar.open("Recurring Export Deleted.", null, {
              duration: 5000
            });
          });
        });
      }
    });
    return false;
  }

  generateRecurring(recurring: PodRecurring) {
    this.printOnDemandService.printOnDemandExecuteRecurringPodNow(recurring.podRecurringId).subscribe((_) => {
      this.snackBar.open("Recurring Export Submitted.", null, {
        duration: 5000
      });
    })
    return false;
  }

  closeInteractiveClicked() {
    this.showPowerBi = false;
  }

  launchPrintNow(layout: PodLayoutEx): boolean {
      this.printNow(layout).then(_ => {});
      return false;
  }

  async printNow(layout: PodLayoutEx) {
    const sourcePages = await this.adminInteractiveService.adminInteractiveGetInteractivePrintOnDemandPages((layout.interactId)).toPromise();

    const targetPages: string[] = [];

    layout.pages.forEach((page) => {
      targetPages.push(page.pbixPageName);
    });

    let bookmarkData = layout.bookmarkData;
    if (layout.bookmarkId) {
      const targetInteractive = this.userState.interactives.find(interactive => interactive.interactId === layout.interactId);
      const bookmark = targetInteractive.bookmarks.find(bookmark => bookmark.bookmarkId === layout.bookmarkId);
      if (bookmark) {
        bookmarkData = bookmark.reportBookmarkState;
      }
    }

    const statusData: PrintOnDemandData = {
      fileType: layout.fileTypeId,
      pages: targetPages,
      interactId: layout.interactId,
      bookmarkJson: bookmarkData
    }

    this.snackBar.openFromComponent(PodStatusSnackbarComponent, {
      data: statusData
    });
  }

  interactiveChanged($event: MatSelectChange) {
    this.app.setLastPodLayoutInteractiveId($event.value.interactId);
  }
}
